import { jwtPayload } from "@insightfulscience/shared-utils/jwt/jwtPayload";

import { isTokenValid } from "../../domain/token";
import routes, { ref } from "../../routes";
import { areTokensValid } from "./status";
import EXCHANGE_TOKEN from "./exchangeToken.mutation.graphql";
import SIGN_OUT from "./signOut.mutation.graphql";

const authService = ({
	storageService: { token, refreshToken, setTokens, clearTokens },
	routingService: { replace, push },
	daClient,
	pureGqlClient,
	publicConfig,
}) => {
	const appId = "sg-njs";

	const isAuthorized = () => areTokensValid(token, refreshToken);

	const toAuth = async (
		{
			locale = "en-us",
			redirectPath: _redirectPath,
			internalRedirectPath,
			email,
			quickFlow = false,
			forceSignOut = false,
		},
		{ withSessionId = false, method = "replace" },
	) => {
		const redirectPath = _redirectPath?.startsWith("/auth") ? routes.myAccount : _redirectPath;

		const { asid } = jwtPayload(token.read()) ?? {};
		const sessionId = withSessionId ? asid : undefined;
		const to = ref(`${publicConfig.DA_DOMAIN}/${locale}/auth`, {
			appId,
			redirectPath,
			internalRedirectPath,
			email,
			sessionId,
			quickFlow: quickFlow.toString(),
			forceSignOut: forceSignOut.toString(),
			...(publicConfig.ENV === "LOCAL"
				? {
						testCallbackUrl: `${publicConfig.LOCAL_URL}${routes.auth.exchange}?internalIdToken=:internalIdToken&redirectPath=:redirectPath`,
						forceSignIn: "true",
				  }
				: {}),
		});

		if (method === "replace") replace(to);
		if (method === "push") push(to);

		return new Promise(() => {});
	};

	const bindSession = async ({ locale = "en-us", bindSessionToken, redirectPath }) => {
		replace(
			ref(`${publicConfig.DA_DOMAIN}/${locale}/auth/bind`, {
				bindSessionToken,
				redirectPath,
			}),
		);

		return new Promise(() => {});
	};

	const exchangeToken = internalIdToken =>
		pureGqlClient(EXCHANGE_TOKEN, { internalIdToken }).then(result =>
			setTokens(result.exchangeTokenV2),
		);

	const signOut = async ({ localOnly = false }) => {
		const rToken = refreshToken.read();

		clearTokens();

		if (localOnly || publicConfig.ENV === "LOCAL") return;
		if (!isTokenValid(rToken)) return;

		const { asid } = jwtPayload(rToken);

		await daClient.post("/auth/signout", asid ? { sessionId: asid } : undefined);

		await pureGqlClient(SIGN_OUT, { refreshToken: rToken });
	};

	return { isAuthorized, toAuth, bindSession, exchangeToken, signOut };
};

export default authService;
